import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { cn } from "@/core/helpers/cn";
import { imgSbankSbankAppBg } from "@/shared/assets/img";
import { RegionTabs, TabsIndicator, TabsList, TabsTrigger } from "@/shared/ui/Tabs";
import { BankBranchHomeTabContainer } from "@/domains/bank/containers/branch/BankBranchHomeTabContainer";
import { BankBranchTariffTabContainer } from "@/domains/bank/containers/branch/BankBranchTariffTabContainer";
import { BankBranchTransactionsTabContainer } from "@/domains/bank/containers/branch/BankBranchTransactionsTabContainer";
import { BankLogoColored } from "@/domains/bank/components/BankLogoColored";

const BASE_TRANSITION = 0.15;

const fadeVariants = {
  initial: {
    opacity: 0,
    scale: 0.98,
    y: 8,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
      opacity: {
        duration: BASE_TRANSITION,
        ease: "easeOut",
      },
      scale: {
        duration: BASE_TRANSITION + 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
      y: {
        duration: BASE_TRANSITION + 0.05,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  },
  exit: {
    opacity: 0,
    scale: 1.01,
    y: -4,
    transition: {
      duration: 0.15,
      ease: [0.55, 0.06, 0.68, 0.19],
      opacity: {
        duration: BASE_TRANSITION,
        ease: "easeIn",
      },
      scale: {
        duration: BASE_TRANSITION - 0.05,
        ease: [0.55, 0.06, 0.68, 0.19],
      },
      y: {
        duration: BASE_TRANSITION - 0.05,
        ease: [0.55, 0.06, 0.68, 0.19],
      },
    },
  },
};

const enum Tab {
  Home,
  Transactions,
  Tariff,
}

function TabContent({ tab, branchId }: { tab: number; branchId: number }) {
  switch (tab) {
    case Tab.Home:
      return <BankBranchHomeTabContainer branchId={branchId} />;

    case Tab.Transactions:
      return <BankBranchTransactionsTabContainer />;

    case Tab.Tariff:
      return <BankBranchTariffTabContainer />;

    default:
      return null;
  }
}

export function BankBranch({ branchId }: { branchId: number }) {
  const [activeTab, setActiveTab] = useState(Tab.Home);

  function handleTabChange(tab: string) {
    setActiveTab(Number(tab));
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden w-[1090px] h-[750px] flex flex-col p-8 pb-7 m-auto rounded-[1rem] bg-dark-900 shadow-lg bg-cover bg-center font-tt-norms-pro",
      )}
      style={{
        backgroundImage: `url(${imgSbankSbankAppBg})`,
      }}
    >
      <header className="flex items-center justify-between mb-7 p-5 rounded-[12px] h-[72px] bg-gradient-to-r from-green-700/30 via-green-700/10 to-green-700/0">
        <BankLogoColored />

        <RegionTabs
          className="relative -left-[40px]"
          value={String(activeTab)}
          onValueChange={(tab) => handleTabChange(tab)}
        >
          <TabsList>
            <TabsTrigger
              className={cn("hover:text-white aria-selected:text-white text-white/60")}
              value={String(Tab.Home)}
            >
              Главная
            </TabsTrigger>

            <TabsTrigger
              className={cn("hover:text-white aria-selected:text-white text-white/60")}
              value={String(Tab.Transactions)}
            >
              Транзакции
            </TabsTrigger>

            <TabsTrigger
              className={cn("hover:text-white aria-selected:text-white text-white/60")}
              value={String(Tab.Tariff)}
            >
              О тарифе
            </TabsTrigger>
          </TabsList>
          <TabsIndicator
            className={cn("bg-gradient-to-r from-[#21A038] to-[#186D27] h-[3px] -bottom-[5px]")}
          />
        </RegionTabs>

        <div />
      </header>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="h-full"
        >
          <TabContent tab={activeTab} branchId={branchId} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
